import React, { useState, useEffect } from 'react';
import styles from './StockList.module.scss';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import stockService from '../../services/stockService';
import { Stock } from '../../types/stockTypes';

const StockList: React.FC<{ role?: string }> = ({ role = 'admin' }) => {
  const [stocks, setStocks] = useState<Stock[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchStocks = async () => {
    try {
      const response = await stockService.getStock();

      const normalizedStocks: Stock[] = Array.isArray(response)
        ? response
            .map((stock): Stock | null => {
              if (!stock || typeof stock !== 'object') {
                console.warn('Invalid stock object:', stock);
                return null;
              }

              return {
                id: stock.id ?? '',
                product_id: stock.product_id ?? '',
                product_name: stock.product_name ?? '',
                price: Number(stock.price) || 0,
                full_stock_count: stock.full_stock_count ?? 0,
                empty_stock_count: stock.empty_stock_count ?? 0,
                employee_id: stock.employee_id ?? '',
                employee_name: stock.employee_name,
                created_at: stock.created_at ?? new Date().toISOString(),
                updated_at: stock.updated_at ?? new Date().toISOString(),
              };
            })
            .filter((stock): stock is Stock => stock !== null)
        : [];

      setStocks(normalizedStocks);
    } catch (err) {
      console.error('Error fetching stocks:', err);
      toast.error('Failed to fetch stocks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStocks();
  }, []);

  const filteredStocks = stocks.filter(stock =>
    (stock.product_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (stock.employee_name?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(price);
  };

  return (
    <div className={styles.container}>
      <h4>Stock Management</h4>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.searchContainer}>
            <div className={styles.searchWrapper}>
              <svg className={styles.searchIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                placeholder="Search stock by product or employee..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
              {searchTerm && (
                <button
                  className={styles.clearButton}
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear search"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Product Name</th>
              <th>Price</th>
              <th>Full Stock</th>
              <th>Empty Stock</th>
              <th>Updated By</th>
              <th>Last Updated</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: '20px' }}>
                  Loading stocks...
                </td>
              </tr>
            ) : (
              Array.isArray(filteredStocks) && filteredStocks.length > 0 ? (
                filteredStocks.map(stock => {
                  if (!stock || !stock.id) {
                    console.warn('Invalid stock in render:', stock);
                    return null;
                  }
                  
                  const isLowStock = (stock.full_stock_count ?? 0) < 10;
                  
                  return (
                    <tr key={stock.id} className={isLowStock ? styles.lowStock : ''}>
                      <td>{stock.product_name || '-'}</td>
                      <td>{formatPrice(stock.price || 0)}</td>
                      <td>
                        <span className={isLowStock ? styles.lowStockBadge : ''}>
                          {stock.full_stock_count ?? 0}
                          {isLowStock && (
                            <svg className={styles.warningIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                              <line x1="12" y1="9" x2="12" y2="13"></line>
                              <line x1="12" y1="17" x2="12.01" y2="17"></line>
                            </svg>
                          )}
                        </span>
                      </td>
                      <td>{stock.empty_stock_count ?? 0}</td>
                      <td>{stock.employee_name || '-'}</td>
                      <td>{new Date(stock.updated_at).toLocaleString('en-IN', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}</td>
                    </tr>
                  );
                }).filter(row => row !== null)
              ) : (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: '20px' }}>
                    {searchTerm ? 'No stocks found matching your search' : 'No stocks found'}
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
      
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default StockList;